import React, { useState, useEffect, useMemo } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps
} from 'recharts';
import {
  TrendingUp,
  Calendar,
  Eye,
  EyeOff,
  BarChart3,
  LineChart as LineChartIcon,
  DollarSign,
  ShoppingCart,
  Grid3x3,
  Zap
} from 'lucide-react';
import { SalesDataService, MonthlySalesData, QuarterlySalesData, SaleRecord } from '../services/salesDataService';

type PeriodView = 'monthly' | 'quarterly' | 'yearly';
type ChartType = 'line' | 'bar';
type MetricType = 'revenue' | 'quantity' | 'avgTicket' | 'plates' | 'kwp';

interface ChartDataPoint {
  name: string;
  currentYear: number;
  previousYear?: number;
  previousQuarter?: number;
  [key: string]: string | number | undefined;
}

interface CustomTooltipProps extends TooltipProps<number, string> {
  showPreviousYear: boolean;
  showPreviousQuarter: boolean;
  historicalYears: number[];
}

// Mapeamento fixo de cor por ano - cada ano sempre terá a mesma cor
const YEAR_COLORS: { [key: number]: string } = {
  2021: '#22c55e',  // Verde
  2022: '#3b82f6',  // Azul
  2023: '#eab308',  // Amarelo
  2024: '#ef4444',  // Vermelho
  2025: '#6b7280',  // Cinza
  2026: '#ec4899'   // Rosa
};

const COLORS = {
  previousQuarter: '#f97316',  // Laranja (apenas para trimestre anterior, não ano)
};

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  showPreviousYear,
  showPreviousQuarter,
  historicalYears
}) => {
  if (!active || !payload || payload.length === 0) return null;

  const currentValue = payload.find(p => p.dataKey === 'currentYear')?.value as number || 0;
  const hasCurrentData = currentValue > 0;

  return (
    <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
      <p className="font-semibold text-gray-800 mb-2">{label}</p>

      {payload.map((entry, index) => {
        const value = entry.value as number;
        let percentChange: number | null = null;
        let comparisonLabel = '';
        const isCurrentYear = entry.dataKey === 'currentYear';
        let showPercentage = false;

        if (!isCurrentYear && hasCurrentData && value > 0) {
          if (entry.dataKey === 'previousYear' && showPreviousYear) {
            percentChange = SalesDataService.calculatePercentageChange(currentValue, value);
            comparisonLabel = 'vs Ano Anterior';
            showPercentage = true;
          } else if (entry.dataKey === 'previousQuarter' && showPreviousQuarter) {
            percentChange = SalesDataService.calculatePercentageChange(currentValue, value);
            comparisonLabel = 'vs Trimestre Anterior';
            showPercentage = true;
          } else if (entry.dataKey.startsWith('year_')) {
            percentChange = SalesDataService.calculatePercentageChange(currentValue, value);
            comparisonLabel = `vs ${entry.name}`;
            showPercentage = true;
          }
        }

        return (
          <div key={index} className="flex items-center justify-between gap-4 py-1">
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-sm text-gray-700">{entry.name}:</span>
            </div>
            <div className="text-right">
              {value > 0 ? (
                <span className="font-semibold text-gray-900">
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                  }).format(value)}
                </span>
              ) : (
                <span className="text-xs text-gray-400 italic">Sem dados</span>
              )}
              {showPercentage && percentChange !== null && (
                <div className={`text-xs ${percentChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {percentChange >= 0 ? '+' : ''}{percentChange.toFixed(1)}% {comparisonLabel}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {!hasCurrentData && (
        <div className="mt-2 pt-2 border-t border-amber-200">
          <div className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
            ⚠ Sem dados do ano de referência para este período
          </div>
        </div>
      )}
    </div>
  );
};

export default function SalesComparisonChart() {
  const [sales, setSales] = useState<SaleRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [periodView, setPeriodView] = useState<PeriodView>('monthly');
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [showPreviousYear, setShowPreviousYear] = useState(true);
  const [showPreviousQuarter, setShowPreviousQuarter] = useState(false);
  const [selectedHistoricalYears, setSelectedHistoricalYears] = useState<Set<number>>(new Set());
  const [visibleMetrics, setVisibleMetrics] = useState<Set<MetricType>>(new Set(['revenue']));

  useEffect(() => {
    loadSalesData();
  }, []);

  const loadSalesData = async () => {
    try {
      setLoading(true);
      const data = await SalesDataService.fetchAllSales();
      setSales(data);

      if (data.length > 0) {
        const years = SalesDataService.getAvailableYears(data);
        if (years.length > 0) {
          setSelectedYear(years[0]);
        }
      }
    } catch (error) {
      console.error('Error loading sales data:', error);
    } finally {
      setLoading(false);
    }
  };

  const availableYears = useMemo(() => {
    return SalesDataService.getAvailableYears(sales);
  }, [sales]);

  const historicalYearsOptions = useMemo(() => {
    return availableYears.filter(y => y !== selectedYear && y !== selectedYear - 1).sort((a, b) => a - b);
  }, [availableYears, selectedYear]);

  const getColorForYear = (year: number): string => {
    return YEAR_COLORS[year] || '#64748b'; // Fallback para anos não mapeados
  };

  const chartData = useMemo((): ChartDataPoint[] => {
    if (sales.length === 0) return [];

    const monthlySales = SalesDataService.aggregateByMonth(sales);
    const quarterlySales = SalesDataService.aggregateByQuarter(sales);
    const yearlySales = SalesDataService.aggregateByYear(sales);

    if (periodView === 'monthly') {
      const currentYearData = SalesDataService.getMonthsForYear(monthlySales, selectedYear);
      const previousYearData = SalesDataService.getMonthsForYear(monthlySales, selectedYear - 1);

      const data: ChartDataPoint[] = [];
      for (let month = 1; month <= 12; month++) {
        const current = currentYearData.find(m => m.month === month);
        const previous = previousYearData.find(m => m.month === month);

        const point: ChartDataPoint = {
          name: SalesDataService.getMonthName(month),
          currentYear: current?.totalValue || 0
        };

        // Adicionar métricas do ano atual
        if (current) {
          point['revenue_current'] = current.totalValue;
          point['quantity_current'] = current.totalSales;
          point['avgTicket_current'] = current.avgTicket;
          point['plates_current'] = current.totalPlates;
          point['kwp_current'] = current.totalKwp;
        }

        // Adicionar métricas do ano anterior
        if (showPreviousYear && previous) {
          point.previousYear = previous.totalValue;
          point['revenue_previous'] = previous.totalValue;
          point['quantity_previous'] = previous.totalSales;
          point['avgTicket_previous'] = previous.avgTicket;
          point['plates_previous'] = previous.totalPlates;
          point['kwp_previous'] = previous.totalKwp;
        }

        Array.from(selectedHistoricalYears).sort((a, b) => a - b).forEach(year => {
          const historicalMonths = SalesDataService.getMonthsForYear(monthlySales, year);
          const historicalData = historicalMonths.find(m => m.month === month);
          if (historicalData) {
            point[`year_${year}`] = historicalData.totalValue;
          }
        });

        data.push(point);
      }

      return data;
    } else if (periodView === 'quarterly') {
      const currentYearData = SalesDataService.getQuartersForYear(quarterlySales, selectedYear);
      const previousYearData = SalesDataService.getQuartersForYear(quarterlySales, selectedYear - 1);

      const data: ChartDataPoint[] = [];
      for (let quarter = 1; quarter <= 4; quarter++) {
        const current = currentYearData.find(q => q.quarter === quarter);
        const previous = previousYearData.find(q => q.quarter === quarter);

        const point: ChartDataPoint = {
          name: `Q${quarter}`,
          currentYear: current?.totalValue || 0
        };

        // Adicionar métricas do trimestre atual
        if (current) {
          point['revenue_current'] = current.totalValue;
          point['quantity_current'] = current.totalSales;
          point['avgTicket_current'] = current.avgTicket;
          point['plates_current'] = current.totalPlates;
          point['kwp_current'] = current.totalKwp;
        }

        if (showPreviousYear && previous) {
          point.previousYear = previous.totalValue;
          point['revenue_previous'] = previous.totalValue;
          point['quantity_previous'] = previous.totalSales;
          point['avgTicket_previous'] = previous.avgTicket;
          point['plates_previous'] = previous.totalPlates;
          point['kwp_previous'] = previous.totalKwp;
        }

        if (showPreviousQuarter && quarter > 1) {
          const prevQ = currentYearData.find(q => q.quarter === quarter - 1);
          if (prevQ) {
            point.previousQuarter = prevQ.totalValue;
          }
        }

        Array.from(selectedHistoricalYears).sort((a, b) => a - b).forEach(year => {
          const historicalQuarters = SalesDataService.getQuartersForYear(quarterlySales, year);
          const historicalData = historicalQuarters.find(q => q.quarter === quarter);
          if (historicalData) {
            point[`year_${year}`] = historicalData.totalValue;
          }
        });

        data.push(point);
      }

      return data;
    } else {
      const data: ChartDataPoint[] = yearlySales.map(y => ({
        name: y.year.toString(),
        currentYear: y.totalValue
      }));

      return data;
    }
  }, [sales, periodView, selectedYear, showPreviousYear, showPreviousQuarter, selectedHistoricalYears]);

  const toggleHistoricalYear = (year: number) => {
    const newSet = new Set(selectedHistoricalYears);
    if (newSet.has(year)) {
      newSet.delete(year);
    } else {
      newSet.add(year);
    }
    setSelectedHistoricalYears(newSet);
  };

  const toggleMetric = (metric: MetricType) => {
    const newSet = new Set(visibleMetrics);
    if (newSet.has(metric)) {
      if (newSet.size > 1) {
        newSet.delete(metric);
      }
    } else {
      newSet.add(metric);
    }
    setVisibleMetrics(newSet);
  };

  const getMetricValue = (data: MonthlySalesData | QuarterlySalesData, metric: MetricType): number => {
    switch (metric) {
      case 'revenue':
        return data.totalValue;
      case 'quantity':
        return data.totalSales;
      case 'avgTicket':
        return data.avgTicket;
      case 'plates':
        return data.totalPlates;
      case 'kwp':
        return data.totalKwp;
      default:
        return 0;
    }
  };

  const getMetricLabel = (metric: MetricType): string => {
    switch (metric) {
      case 'revenue':
        return 'Faturamento';
      case 'quantity':
        return 'Quantidade';
      case 'avgTicket':
        return 'Ticket Médio';
      case 'plates':
        return 'Placas';
      case 'kwp':
        return 'kWp';
      default:
        return '';
    }
  };

  const getMetricColor = (metric: MetricType): string => {
    switch (metric) {
      case 'revenue':
        return '#3b82f6';
      case 'quantity':
        return '#10b981';
      case 'avgTicket':
        return '#f59e0b';
      case 'plates':
        return '#8b5cf6';
      case 'kwp':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const renderChart = () => {
    const ChartComponent = chartType === 'line' ? LineChart : BarChart;
    const DataComponent = chartType === 'line' ? Line : Bar;

    return (
      <ResponsiveContainer width="100%" height={400}>
        <ChartComponent data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="name"
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
          />
          <YAxis
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
            tickFormatter={(value) => {
              return new Intl.NumberFormat('pt-BR', {
                notation: 'compact',
                compactDisplay: 'short'
              }).format(value);
            }}
          />
          <Tooltip
            content={
              <CustomTooltip
                showPreviousYear={showPreviousYear}
                showPreviousQuarter={showPreviousQuarter}
                historicalYears={Array.from(selectedHistoricalYears)}
              />
            }
          />
          <Legend />

          {Array.from(visibleMetrics).map((metric) => (
            <DataComponent
              key={`${metric}_current`}
              dataKey={`${metric}_current`}
              name={`${getMetricLabel(metric)} ${selectedYear}`}
              fill={getMetricColor(metric)}
              stroke={getMetricColor(metric)}
              strokeWidth={chartType === 'line' ? 3 : 0}
              opacity={0.8}
            />
          ))}

          {showPreviousYear && Array.from(visibleMetrics).map((metric) => (
            <DataComponent
              key={`${metric}_previous`}
              dataKey={`${metric}_previous`}
              name={`${getMetricLabel(metric)} ${selectedYear - 1}`}
              fill={getMetricColor(metric)}
              stroke={getMetricColor(metric)}
              strokeWidth={chartType === 'line' ? 2 : 0}
              opacity={0.5}
              strokeDasharray={chartType === 'line' ? '5 5' : undefined}
            />
          ))}

          {showPreviousQuarter && periodView === 'quarterly' && (
            <DataComponent
              dataKey="previousQuarter"
              name="Trimestre Anterior"
              fill={COLORS.previousQuarter}
              stroke={COLORS.previousQuarter}
              strokeWidth={chartType === 'line' ? 2 : 0}
            />
          )}

          {Array.from(selectedHistoricalYears).sort((a, b) => a - b).map((year) => (
            <DataComponent
              key={year}
              dataKey={`year_${year}`}
              name={`${year}`}
              fill={getColorForYear(year)}
              stroke={getColorForYear(year)}
              strokeWidth={chartType === 'line' ? 2 : 0}
              opacity={0.7}
            />
          ))}
        </ChartComponent>
      </ResponsiveContainer>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6">
        <div className="flex items-center gap-3 text-white">
          <TrendingUp className="w-8 h-8" />
          <div>
            <h2 className="text-2xl font-bold">Análise Comparativa de Vendas</h2>
            <p className="text-blue-100 text-sm mt-1">
              Compare vendas entre diferentes períodos e anos
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-2" />
              Ano de Referência
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Visualização por Período
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setPeriodView('monthly')}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                  periodView === 'monthly'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Mensal
              </button>
              <button
                onClick={() => setPeriodView('quarterly')}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                  periodView === 'quarterly'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Trimestral
              </button>
              <button
                onClick={() => setPeriodView('yearly')}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                  periodView === 'yearly'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Anual
              </button>
            </div>
          </div>
        </div>

        <div className="border-t pt-4">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Métricas Visíveis
          </label>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
            <button
              onClick={() => toggleMetric('revenue')}
              className={`flex items-center justify-between px-3 py-2 rounded-lg border-2 transition-all ${
                visibleMetrics.has('revenue')
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                <span className="text-sm font-medium">Faturamento</span>
              </div>
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: visibleMetrics.has('revenue') ? getMetricColor('revenue') : '#d1d5db' }}
              />
            </button>

            <button
              onClick={() => toggleMetric('quantity')}
              className={`flex items-center justify-between px-3 py-2 rounded-lg border-2 transition-all ${
                visibleMetrics.has('quantity')
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-4 h-4" />
                <span className="text-sm font-medium">Quantidade</span>
              </div>
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: visibleMetrics.has('quantity') ? getMetricColor('quantity') : '#d1d5db' }}
              />
            </button>

            <button
              onClick={() => toggleMetric('avgTicket')}
              className={`flex items-center justify-between px-3 py-2 rounded-lg border-2 transition-all ${
                visibleMetrics.has('avgTicket')
                  ? 'border-amber-500 bg-amber-50 text-amber-700'
                  : 'border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                <span className="text-sm font-medium">Ticket Médio</span>
              </div>
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: visibleMetrics.has('avgTicket') ? getMetricColor('avgTicket') : '#d1d5db' }}
              />
            </button>

            <button
              onClick={() => toggleMetric('plates')}
              className={`flex items-center justify-between px-3 py-2 rounded-lg border-2 transition-all ${
                visibleMetrics.has('plates')
                  ? 'border-purple-500 bg-purple-50 text-purple-700'
                  : 'border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center gap-2">
                <Grid3x3 className="w-4 h-4" />
                <span className="text-sm font-medium">Placas</span>
              </div>
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: visibleMetrics.has('plates') ? getMetricColor('plates') : '#d1d5db' }}
              />
            </button>

            <button
              onClick={() => toggleMetric('kwp')}
              className={`flex items-center justify-between px-3 py-2 rounded-lg border-2 transition-all ${
                visibleMetrics.has('kwp')
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : 'border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4" />
                <span className="text-sm font-medium">kWp</span>
              </div>
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: visibleMetrics.has('kwp') ? getMetricColor('kwp') : '#d1d5db' }}
              />
            </button>
          </div>
        </div>

        <div className="border-t pt-4">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Controles de Visualização
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              onClick={() => setShowPreviousYear(!showPreviousYear)}
              className={`flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-all ${
                showPreviousYear
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 bg-gray-50 text-gray-500'
              }`}
            >
              <div className="flex items-center gap-2">
                {showPreviousYear ? (
                  <Eye className="w-5 h-5" />
                ) : (
                  <EyeOff className="w-5 h-5" />
                )}
                <span className="font-medium">Ano Anterior ({selectedYear - 1})</span>
              </div>
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: showPreviousYear ? getColorForYear(selectedYear - 1) : '#d1d5db' }}
              />
            </button>

            {periodView === 'quarterly' && (
              <button
                onClick={() => setShowPreviousQuarter(!showPreviousQuarter)}
                className={`flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-all ${
                  showPreviousQuarter
                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                    : 'border-gray-300 bg-gray-50 text-gray-500'
                }`}
              >
                <div className="flex items-center gap-2">
                  {showPreviousQuarter ? (
                    <Eye className="w-5 h-5" />
                  ) : (
                    <EyeOff className="w-5 h-5" />
                  )}
                  <span className="font-medium">Trimestre Anterior</span>
                </div>
                <div
                  className={`w-3 h-3 rounded-full ${
                    showPreviousQuarter ? 'bg-orange-500' : 'bg-gray-300'
                  }`}
                  style={{ backgroundColor: showPreviousQuarter ? COLORS.previousQuarter : undefined }}
                />
              </button>
            )}
          </div>
        </div>

        {historicalYearsOptions.length > 0 && periodView !== 'yearly' && (
          <div className="border-t pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Anos Históricos para Comparação
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {historicalYearsOptions.map((year) => (
                <button
                  key={year}
                  onClick={() => toggleHistoricalYear(year)}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-all ${
                    selectedHistoricalYears.has(year)
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span className="font-medium">{year}</span>
                  <div
                    className={`w-3 h-3 rounded-full`}
                    style={{
                      backgroundColor: selectedHistoricalYears.has(year)
                        ? getColorForYear(year)
                        : '#d1d5db'
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Tipo de Gráfico
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setChartType('bar')}
                className={`p-2 rounded-lg transition-all ${
                  chartType === 'bar'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title="Gráfico de Barras"
              >
                <BarChart3 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setChartType('line')}
                className={`p-2 rounded-lg transition-all ${
                  chartType === 'line'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title="Gráfico de Linhas"
              >
                <LineChartIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            {renderChart()}
          </div>
        </div>

        <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-4">
          <div className="flex items-start gap-3">
            <TrendingUp className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold mb-1">Análise de Sazonalidade</p>
              <p className="text-blue-800">
                Compare vendas do mesmo período em anos diferentes para identificar padrões sazonais
                e tendências de crescimento. Use os controles acima para mostrar/ocultar diferentes períodos.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
